/**
 * Lightweight WYSIWYG editor using contentEditable + execCommand.
 * No external dependencies. Supports bold, italic, underline, lists, links.
 * Output is HTML string — compatible with dangerouslySetInnerHTML on render.
 */
import React from "react";
import {
    Bold, Italic, Underline, List, ListOrdered,
    Link, Unlink, RemoveFormatting
} from "lucide-react";

const BTN = "p-1.5 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground disabled:opacity-30";

function ToolBtn({ title, onClick, children }) {
    return (
        <button type="button" title={title} onMouseDown={e => { e.preventDefault(); onClick(); }} className={BTN}>
            {children}
        </button>
    );
}

export default function RichTextEditor({ value, onChange, placeholder = "Descriere completă..." }) {
    const ref = React.useRef(null);
    const skipSync = React.useRef(false);

    // Sync external value → editor (only on initial load / external reset)
    React.useEffect(() => {
        if (!ref.current) return;
        if (skipSync.current) { skipSync.current = false; return; }
        if (ref.current.innerHTML !== (value || "")) {
            ref.current.innerHTML = value || "";
        }
    }, [value]);

    const exec = (cmd, arg = null) => {
        ref.current?.focus();
        document.execCommand(cmd, false, arg);
        emit();
    };

    const emit = () => {
        skipSync.current = true;
        onChange(ref.current?.innerHTML || "");
    };

    const insertLink = () => {
        const url = window.prompt("URL link:", "https://");
        if (url) exec("createLink", url);
    };

    const cleanPaste = (e) => {
        e.preventDefault();
        // Strip styles but keep structure (bold/italic/lists/links)
        const html = e.clipboardData.getData("text/html");
        const text = e.clipboardData.getData("text/plain");
        if (html) {
            const tmp = document.createElement("div");
            tmp.innerHTML = html;
            // Remove style attrs, class attrs, keep semantic tags
            tmp.querySelectorAll("*").forEach(el => {
                el.removeAttribute("style");
                el.removeAttribute("class");
                el.removeAttribute("id");
            });
            // Remove unwanted tags but keep content
            ["script","meta","head","link","svg","img"].forEach(tag => {
                tmp.querySelectorAll(tag).forEach(n => n.remove());
            });
            document.execCommand("insertHTML", false, tmp.innerHTML);
        } else {
            document.execCommand("insertText", false, text);
        }
        emit();
    };

    return (
        <div className="border border-border rounded-xl overflow-hidden bg-card focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 transition-all">
            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-border bg-muted/40">
                <ToolBtn title="Aldin (Ctrl+B)" onClick={() => exec("bold")}><Bold className="h-3.5 w-3.5" /></ToolBtn>
                <ToolBtn title="Cursiv (Ctrl+I)" onClick={() => exec("italic")}><Italic className="h-3.5 w-3.5" /></ToolBtn>
                <ToolBtn title="Subliniat (Ctrl+U)" onClick={() => exec("underline")}><Underline className="h-3.5 w-3.5" /></ToolBtn>
                <div className="w-px h-4 bg-border mx-1" />
                <ToolBtn title="Listă cu puncte" onClick={() => exec("insertUnorderedList")}><List className="h-3.5 w-3.5" /></ToolBtn>
                <ToolBtn title="Listă numerotată" onClick={() => exec("insertOrderedList")}><ListOrdered className="h-3.5 w-3.5" /></ToolBtn>
                <div className="w-px h-4 bg-border mx-1" />
                <ToolBtn title="Adaugă link" onClick={insertLink}><Link className="h-3.5 w-3.5" /></ToolBtn>
                <ToolBtn title="Elimină link" onClick={() => exec("unlink")}><Unlink className="h-3.5 w-3.5" /></ToolBtn>
                <div className="w-px h-4 bg-border mx-1" />
                <ToolBtn title="Elimină formatare" onClick={() => exec("removeFormat")}><RemoveFormatting className="h-3.5 w-3.5" /></ToolBtn>
            </div>
            {/* Editable area — iframes get pointer-events:none so they can be selected/deleted */}
            <style>{`[data-rte] iframe { pointer-events: none; opacity: 0.7; outline: 2px dashed #94a3b8; cursor: default; }`}</style>
            <div
                ref={ref}
                data-rte
                contentEditable
                suppressContentEditableWarning
                onInput={emit}
                onPaste={cleanPaste}
                data-placeholder={placeholder}
                className="min-h-[160px] px-4 py-3 text-sm leading-relaxed outline-none empty:before:content-[attr(data-placeholder)] empty:before:text-muted-foreground prose prose-sm prose-neutral max-w-none"
            />
        </div>
    );
}
